/**
 * @file     rateLimiter.ts
 * @readme   ./CLAUDE.md
 * @purpose  IP-based rate limiter for Route Handlers. Primary path uses the distributed Supabase-backed limiter so state is shared across Vercel Fluid Compute instances; falls back to an in-memory map when the distributed store is unavailable.
 */
import { checkDistributedRateLimit } from './distributedRateLimiter';

const SHORT_WINDOW_MS = 15 * 60 * 1000;
const LONG_WINDOW_MS = 60 * 60 * 1000;
const SHORT_WINDOW_LIMIT = 5;
const LONG_WINDOW_LIMIT = 20;

export interface RateLimitResult {
  allowed: boolean;
  approaching: boolean;
  retryAfterSeconds?: number;
}

/**
 * Check whether the IP has exceeded the short-window (5 / 15min) or
 * long-window (20 / 60min) lead-submission cap.
 *
 * The previous implementation was sync + in-memory; this is now async so
 * the bucket counters live in Supabase Postgres and survive across
 * concurrent Vercel function instances. The function signature otherwise
 * matches the original — a single `ip` string in, a `RateLimitResult` out.
 */
export async function checkRateLimit(ip: string): Promise<RateLimitResult> {
  const short = await checkDistributedRateLimit(
    'antispam:short',
    'short',
    ip,
    SHORT_WINDOW_MS,
    SHORT_WINDOW_LIMIT,
  );
  if (!short.allowed) {
    return {
      allowed: false,
      approaching: false,
      retryAfterSeconds: short.retryAfterSeconds || 900,
    };
  }

  const long = await checkDistributedRateLimit(
    'antispam:long',
    'long',
    ip,
    LONG_WINDOW_MS,
    LONG_WINDOW_LIMIT,
  );
  if (!long.allowed) {
    return {
      allowed: false,
      approaching: false,
      retryAfterSeconds: long.retryAfterSeconds || 3600,
    };
  }

  const shortRemaining = Math.max(0, SHORT_WINDOW_LIMIT - short.count);
  const longRemaining = Math.max(0, LONG_WINDOW_LIMIT - long.count);
  const approaching =
    (shortRemaining > 0 && shortRemaining <= 2) ||
    (longRemaining > 0 && longRemaining <= 2);

  return { allowed: true, approaching };
}
